import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import {
  $transferRequestsGetDocumentsStore,
  $transferRequestsUploadFileStore,
  $transferRequestsDeleteFileStore,
  $transferUpdateStatusStore,
  $generateOTPCodeStore,
  $downloadDocumentStore,
} from '@lib/store';
import { AmountUtils, DateUtils, PaletteUtils } from '@shared/utils';
import { TransferRequestStatusEnum, type TransferRequestResponse } from '@shared/api/rest/transferRequests';
import { GenerateCodeTypes } from '@shared/api/rest/users';
import { TransferModalType } from '@pages';
import { Button, ColorsEnum, FileUpload, Icon, Modal, Spinner, Text } from '@shared/widgets';
import { ExchangeRate } from '@shared/widgets/exchangeRate';
import WarningIcon from '@assets/icons/warning.svg';
import DownloadIcon from '@assets/icons/download.svg';

interface PropsType {
  transferData: TransferRequestResponse;
  onClose: () => void;
  setOTPModal?: (data: TransferRequestResponse) => void;
  closeOTPModal?: () => void;
  readOnly?: boolean;
  cancelLabel?: string;
  confirmLabel?: string;
  transactionId?: string;
  noDocumentUpload?: boolean;
  extraSubmitHandle?: () => void | Promise<void>;
  isLimitReached?: boolean;
  isApproval?: boolean;
  updateGridsData?: () => void;
  setTransfer?: (value: TransferModalType) => void;
  isOpen: boolean;
}

export const TransferRequestConfirmModal = (props: PropsType) => {
  function splitCamelCase(str: string) {
    return str.replace(/([A-Z])/g, ' $1').trim();
  }
  const leftColPropsMap = [
    {
      label: 'Type of transfer/Payment',
      value: splitCamelCase(props.transferData.type),
    },
    {
      label: 'Date of execution',
      value: DateUtils.formatDate(props.transferData.startDate || props.transferData.createdOn || ''),
    },
    {
      label: 'Recipient',
      value: props.transferData.beneficiaryAccount?.name || props.transferData.recipient?.name,
    },
    { label: 'Sender Currency', value: props.transferData.senderCurrency },
    {
      label: 'Fast Payment',
      value: props.transferData.fastPayment ? 'Yes' : 'No',
    },
    props.transferData.beneficiaryCurrency !== props.transferData.senderCurrency
      ? {
          label: 'Exchange rate',
          value: `${AmountUtils.transformToAmountWithCurrency(1, props.transferData.senderCurrency)} = ${AmountUtils.transformToAmountWithCurrency(props.transferData.exchangeRate, props.transferData.beneficiaryCurrency as string)}`,
          additionalComponent: <ExchangeRate />,
        }
      : null,
  ];

  const rightColPropsMap = [
    {
      label: 'Beneficiary Currency',
      value: props.transferData.beneficiaryCurrency,
    },
    {
      label: 'Sender Amount',
      value: `${AmountUtils.transformToAmountWithCurrency(props.transferData.senderAmount, props.transferData.senderCurrency)}`,
    },

    {
      label: 'Beneficiary Amount',
      value: `${AmountUtils.transformToAmountWithCurrency(props.transferData.beneficiaryAmount, props.transferData.beneficiaryCurrency)}`,
    },
    {
      label: 'Fees',
      value: `${AmountUtils.transformToAmountWithCurrency(props.transferData.fee, props.transferData.senderCurrency)}`,
    },
    {
      label: 'Total Amount',
      value: `${AmountUtils.transformToAmountWithCurrency(
        typeof props.transferData.fee !== 'number' ? props.transferData.senderAmount : props.transferData.totalAmount,
        props.transferData.senderCurrency,
      )}`,
    },
  ];

  const [selectedItems, setSelectedItems] = useState<File[]>([]);
  const [isTransactionProcessing, setIsTransactionProcessing] = useState<boolean>(false);

  const uploadedDocumentsStore = useUnit($transferRequestsGetDocumentsStore.store);
  const updateStatusStore = useUnit($transferUpdateStatusStore.store);

  const docsUploadStore = useUnit($transferRequestsUploadFileStore.store);
  const docsDeleteStore = useUnit($transferRequestsDeleteFileStore.store);

  useEffect(() => {
    if (props.transactionId) {
      $transferRequestsGetDocumentsStore.events.update({
        TransferRequestId: props.transactionId,
      });
    }
  }, [props.transactionId, props.readOnly, $transferRequestsGetDocumentsStore]);

  const onRejectHandle = async () => {
    await $transferUpdateStatusStore.events.update({
      id: props.transferData.id,
      status: TransferRequestStatusEnum.Cancelled,
    });
    props.updateGridsData?.();
    props.closeOTPModal?.();
    props.setTransfer?.(TransferModalType.REJECT);
    props.onClose();
  };

  const onSubmitHandle = async () => {
    setIsTransactionProcessing(true);
    try {
      await props.extraSubmitHandle?.();
      if (props.transferData.status === TransferRequestStatusEnum.WaitingForApproval) {
        await $generateOTPCodeStore.events.update({
          OtpType: GenerateCodeTypes.UpdateTransferRequest,
        });
      }
      setIsTransactionProcessing(false);
      props.setOTPModal?.(props.transferData);
    } catch {
      props.onClose();
      setIsTransactionProcessing(false);
    }
  };

  const onFileUpload = async (file: File) => {
    if (props.transactionId) {
      await $transferRequestsUploadFileStore.events.update({
        transferRequestId: props.transactionId,
        file,
      });
    }
  };

  const onDocDelete = async (id: string) => {
    await $transferRequestsDeleteFileStore.events.update({
      TransferRequestId: props.transactionId!,
      DocumentId: id,
    });
  };

  useEffect(() => {
    $transferRequestsGetDocumentsStore.events.update({
      TransferRequestId: props.transactionId!,
    });
  }, [docsDeleteStore, docsUploadStore]);

  const onFileDelete = async (file: File) => {
    $transferRequestsDeleteFileStore.events.update({
      TransferRequestId: props.transactionId!,
      DocumentId: (selectedItems.find((item) => item.name === file.name) as File & { id: string })!.id,
    });
  };

  const handleDownload = (id: string) => $downloadDocumentStore.events.download({ TransferRequestId: props.transactionId!, DocumentId: id });

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={
        <>
          <div className='flex justify-between items-center'>
            <Text className='font-[Prata]' size='3xl'>
              {AmountUtils.transformToAmountWithCurrency(
                typeof props.transferData.fee !== 'number' ? props.transferData.senderAmount : props.transferData.totalAmount,
                props.transferData.senderCurrency,
              )}
            </Text>
          </div>
          <Text size='sm'>Fee: {AmountUtils.transformToAmountWithCurrency(props.transferData.fee, props.transferData.senderCurrency)}</Text>
          {(props.transferData.startDate || props.transferData.createdOn) && (
            <Text size='sm'>{DateUtils.formatDate(props.transferData.startDate || props.transferData.createdOn || '', { withTime: true })}</Text>
          )}
        </>
      }
      width='500px'
    >
      {isTransactionProcessing && (
        <div className='absolute top-0 left-0 bg-black bg-opacity-60 w-full h-full z-10 flex items-center justify-center'>
          <Spinner />
        </div>
      )}
      <>
        {!props.readOnly && (
          <div className='flex gap-3 mt-5'>
            <Button color='outlined' className='rounded' disabled={updateStatusStore.loading} fullWidth onClick={onRejectHandle}>
              {props.cancelLabel || 'Reject'}
            </Button>
            <Button
              color='primary'
              className='rounded'
              fullWidth
              onClick={onSubmitHandle}
              disabled={props.isLimitReached && uploadedDocumentsStore.data.length === 0}
            >
              {props.confirmLabel || 'Approve'}
            </Button>
          </div>
        )}
        <div className='flex mt-4 p-3 bg-ice dark:bg-soft-black mb-3'>
          <div
            className={clsx(
              'items-center flex flex-none justify-center h-9 w-9 text-ice mr-3 rounded-3xl',
              PaletteUtils.isInsigniaPalette && 'bg-soft-black dark:bg-primary',
              PaletteUtils.isVcfcPalette && 'bg-primary',
            )}
          >
            {props.transferData.fromAccount?.name[0] || 'A'}
          </div>
          <div className='flex flex-col justify-between ml-3 my-auto'>
            <Text>{props.transferData.fromAccount?.name}</Text>
          </div>
        </div>
        <div className='w-full flex'>
          <div className='w-1/2 flex flex-col items-start justify-start'>
            {leftColPropsMap?.map((element) => (
              <div key={element?.label} className='mb-3'>
                <Text size='sm' color={ColorsEnum.Secondary}>
                  {element?.label}
                </Text>
                <Text weight='semibold' size='sm' className='flex gap-2'>
                  {element?.value}
                  {element?.additionalComponent}
                </Text>
              </div>
            ))}
          </div>
          <div className='w-1/2 flex flex-col items-start justify-start'>
            {rightColPropsMap?.map((element) => (
              <div key={element?.label} className='mb-3'>
                <Text size='sm' color={ColorsEnum.Secondary}>
                  {element?.label}
                </Text>
                <Text weight='semibold' size='sm'>
                  {element?.value}
                </Text>
              </div>
            ))}
          </div>
        </div>
        {props.isLimitReached && !props.readOnly && !props.noDocumentUpload && (
          <>
            <div className='border-none rounded-sm pb-0.5 w-full bg-[var(--input-bg-color)] mt-4 p-4'>
              <Text size='sm' color={ColorsEnum.Secondary}>
                Supporting document
              </Text>
              <div className='flex items-start justify-start mt-5'>
                <div className='p-3 bg-light-carmine-pink rounded-sm mr-3'>
                  <Icon icon={<WarningIcon />} color='red' />
                </div>
                <div className='flex flex-col items-start justify-start'>
                  <Text>
                    To complete the transaction, a supporting documentation is required. Please attach the respective supporting document like but not
                    limited to
                  </Text>
                  <ul className='mt-2 pl-4'>
                    <li className='list-disc'>Agreements</li>
                    <li className='list-disc'>Invoices</li>
                    <li className='list-disc'>Receipts</li>
                  </ul>
                </div>
              </div>
            </div>
            {docsUploadStore.loading ? (
              <Spinner />
            ) : (
              <FileUpload
                setSelectedItems={setSelectedItems}
                onFileUpload={onFileUpload}
                onFileDelete={onFileDelete}
                uploadedDocs={uploadedDocumentsStore.data}
                onDocDelete={onDocDelete}
                status={props.transferData.status}
              />
            )}
          </>
        )}
        {(props.readOnly || props?.isApproval) && props.transactionId && uploadedDocumentsStore.data.length > 0 && (
          <div className='mt-2 flex flex-col items-start justify-start gap-2'>
            {uploadedDocumentsStore.data.map((e) => {
              return (
                <Button key={e.id} color='icon' className='!gap-1' onClick={() => handleDownload(e.id)}>
                  <Icon icon={<DownloadIcon />} color='primary' /> {e.name}
                </Button>
              );
            })}
          </div>
        )}
      </>
    </Modal>
  );
};
